import React, { useState, useEffect, lazy, Suspense } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import "./videoInterview.scss";
import VerificationPage from "./VerificationPage";
import { validateVerificationCode } from "../../Redux/ActionCreators/videoInterviewAction";
import { fetchTenantBranding } from "../../Redux/ActionCreators/qualifyQuestionsAction";

const VideoInterviewMain = lazy(() => import("./VideoInterviewMain"));

let isRequestingFullScreen = false;

const goFullScreen = () => {
  const element = document.documentElement;
  if (element.requestFullscreen) return element.requestFullscreen();
  if (element.mozRequestFullScreen) return element.mozRequestFullScreen();
  if (element.webkitRequestFullscreen) return element.webkitRequestFullscreen();
  if (element.msRequestFullscreen) return element.msRequestFullscreen();
  return Promise.reject("Fullscreen API is not supported.");
};

const attemptFullScreen = () => {
  if (isRequestingFullScreen || document.fullscreenElement) return;

  isRequestingFullScreen = true;
  goFullScreen()
    .catch((err) => {
      console.warn("Fullscreen error:", err.message);
    })
    .finally(() => {
      setTimeout(() => {
        isRequestingFullScreen = false;
      }, 1000);
    });
};

const VideoInterview = () => {
  const { token: videoCallId } = useParams();
  const [searchParams] = useSearchParams();
  const [verificationCode, setVerificationCode] = useState("");
  const [step, setStep] = useState(-1);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const [brandingLoading, setBrandingLoading] = useState(false);

  const dispatch = useDispatch();
  const { connectionDetails, validating } = useSelector((state) => state.videoInterview);
  const tenantBranding = useSelector((state) => state.qualifyQuestions?.tenantBranding);
  const isWhiteLabel = Boolean(tenantBranding?.is_white_label);
  const logoSrc = isWhiteLabel && tenantBranding?.logo
    ? tenantBranding.logo
    : "/images/gappeoai-logo.png";

  useEffect(() => {
    const token = searchParams.get("t");
    if (!token) return;
    setBrandingLoading(true);
    dispatch(fetchTenantBranding(token)).finally(() => setBrandingLoading(false));
  }, [searchParams, dispatch]);

  useEffect(() => {
    if (isWhiteLabel && tenantBranding?.colour) {
      document.documentElement.style.setProperty("--qualify-accent-color", tenantBranding.colour);
    }
  }, [isWhiteLabel, tenantBranding?.colour]);

  const handleVerification = async () => {
    attemptFullScreen();
    const result = await dispatch(validateVerificationCode(videoCallId, verificationCode));
    if (result.success) {
      attemptFullScreen(); // re-request fullscreen on success
      setStep(0); // this triggers main interview UI
    } else {
      setSnackbar({ open: true, message: result.message || "Invalid verification code", severity: "error" });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setSnackbar({ open: true, message: "You cannot exit full screen", severity: "error" });
        setTimeout(() => attemptFullScreen(), 100);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (brandingLoading) {
    return (
      <div className="job-summary">
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="video-interview">
        {step === -1 ? (
          <VerificationPage
            code={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            onSubmit={handleVerification}
            validating={validating}
            logoSrc={logoSrc}
          />
        ) : (
          <Suspense fallback={<div>Loading interview...</div>}>
            <VideoInterviewMain connectionDetails={connectionDetails} logoSrc={logoSrc} />
          </Suspense>
        )}

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
      <div className="video-interview-sm">
        <p>Video interview needs to be taken on Laptop or desktop.</p>
      </div>
    </div>
  );
};

export default VideoInterview;
